
  
document.addEventListener('DOMContentLoaded', function () {
  // Function to initialize a single accordion container
  function initAccordion(container) {
    if (!container) return;

    /* ── RE-ENTRANCY GUARD (Shopify port) ───────────────────────────────────────────
       In the static build this file was included exactly once. In the theme every
       section that owns an accordion loads it itself, because a section cannot know
       what else the merchant has put on the page. Two such sections on one page (Band
       LP: the product block and the FAQ) means two identical <script src> tags — and
       duplicate classic scripts EXECUTE once per tag, they are not de-duplicated the
       way modules are. Each execution registered another DOMContentLoaded handler and
       bound a second click listener to every question, so a click ran the toggle twice:
       the first opened the row, the second saw `.active` already set and closed it
       again. The row never appeared to open at all.

       The flag lives on the container element, not on window, so `window.nooa.reinject`
       still works: the editor swaps in fresh DOM, which carries no flag and binds
       normally, while the containers already wired are left alone. */
    if (container.__nooaAccordionBound) return;
    container.__nooaAccordionBound = true;

    const items = Array.from(container.querySelectorAll('.accordion-item'));
    const answers = items.map(i => i.querySelector('.accordion-answer')).filter(Boolean);
    const useRotateIcon = container.classList.contains('rotate-icon');
    const inlineClosedIconTemplate = container.querySelector('[data-accordion-icon="closed"]');
    const inlineOpenIconTemplate = container.querySelector('[data-accordion-icon="open"]');

    // Check if first item should be open by default
    const openFirst = container.classList.contains('accordion-open-first');

    // Initialize collapsed
    answers.forEach((ans, index) => {
      if (openFirst && index === 0) {
        // First item should be open - set height to scrollHeight first, then to auto
        ans.style.height = ans.scrollHeight + 'px';
        ans.setAttribute('aria-hidden', 'false');
        // Use requestAnimationFrame to ensure proper rendering before setting to auto
        requestAnimationFrame(() => {
          ans.style.height = 'auto';
        });
        const firstItem = items[0];
        if (firstItem) {
          const firstBtn = firstItem.querySelector('.accordion-question');
          const firstIcon = firstItem.querySelector('.accordion-icon');
          if (firstBtn) firstBtn.setAttribute('aria-expanded', 'true');
          if (firstIcon) setIconState(firstIcon, true);
          firstItem.classList.add('active');
        }
      } else {
        // All other items collapsed
        ans.style.height = '0px';
        ans.setAttribute('aria-hidden', 'true');
      }
    });

    // Ensure icon placeholders exist in every item
    items.forEach(item => {
      const btn = item.querySelector('.accordion-question');
      if (!btn) return;
      let icon = item.querySelector('.accordion-icon');
      if (!icon) {
        icon = document.createElement('span');
        icon.className = 'accordion-icon';
        icon.setAttribute('aria-hidden', 'true');
        btn.appendChild(icon);
      }
    });

    // Ensure initial icons are correct
    items.forEach((item, index) => {
      const icon = item.querySelector('.accordion-icon');
      if (icon) {
        setIconState(icon, openFirst && index === 0);
      }
    });

    function createIconNode(templateRef) {
      if (!templateRef) return null;

      // Preferred approach: <template data-accordion-icon="..."><svg>...</svg></template>
      if (templateRef.tagName === 'TEMPLATE') {
        const node = templateRef.content.firstElementChild;
        if (!node) return null;
        const clone = node.cloneNode(true);
        clone.removeAttribute('data-accordion-icon');
        clone.removeAttribute('hidden');
        clone.removeAttribute('aria-hidden');
        return clone;
      }

      // Backward compatibility for direct SVG in container.
      const clone = templateRef.cloneNode(true);
      clone.removeAttribute('data-accordion-icon');
      clone.removeAttribute('hidden');
      clone.removeAttribute('aria-hidden');
      return clone;
    }

    function setIconState(icon, isOpen) {
      if (!icon) return;
      if (useRotateIcon) {
        if (icon.tagName === 'IMG') {
          const replacement = document.createElement('span');
          replacement.className = icon.className;
          replacement.setAttribute('aria-hidden', 'true');
          icon.replaceWith(replacement);
          icon = replacement;
        }
        if (!icon.firstChild && inlineClosedIconTemplate) {
          const rotateSvg = createIconNode(inlineClosedIconTemplate);
          if (rotateSvg) icon.replaceChildren(rotateSvg);
        }
        if (!icon.firstChild) {
          // No icon template provided for this accordion container.
          return;
        }
        icon.style.transition = icon.style.transition || 'transform 0.2s ease';
        icon.style.transform = isOpen ? 'rotate(45deg)' : 'rotate(0deg)';
        return;
      }

      const template = isOpen ? inlineOpenIconTemplate : inlineClosedIconTemplate;
      if (!template) return;

      if (icon.tagName === 'IMG') {
        const replacement = document.createElement('span');
        replacement.className = icon.className;
        replacement.setAttribute('aria-hidden', 'true');
        icon.replaceWith(replacement);
        icon = replacement;
      }

      const svgNode = createIconNode(template);
      if (!svgNode) return;
      icon.replaceChildren(svgNode);
      icon.dataset.iconMode = 'inline-svg';
    }

    function closeItem(item) {
      if (!item) return;
      const btn = item.querySelector('.accordion-question');
      const ans = item.querySelector('.accordion-answer');
      const icon = item.querySelector('.accordion-icon');
      if (!ans) return;

      /* ── PER-RUN WORK-AROUND for flow-defects.md FD-22 (nooa-landing part6, QA) ──────────
         This file was byte-identical to pipeline/templates/code/accordion/accordion.js and
         now DELIBERATELY DIVERGES by these two guards. The template defect is logged as
         FD-22 and remains the owner's to fix; the flow is never edited mid-run.

         The bug: openItem() arms an anonymous `transitionend` listener to hand
         `height: <px>` over to `height: auto`, and closeItem() never removed it. Close a row
         within the 0.3s transition (a double-tap, or an impatient re-tap on a 19.8px-tall
         mobile head) and that listener fires on the CLOSING transition instead, setting
         `height: auto` on a row whose flags all say closed — leaving content visible but
         `aria-hidden="true"`, i.e. unreachable to a screen reader.
         Reproduced 6/6 at 50/120/200ms, clean 4/4 at 280/350ms — the threshold is exactly
         the transition duration. Nothing upstream caught it because every stage runs at
         `--wait 1500`, which is LONGER than the transition: the mitigation for FD-19(b) is
         what hid this one.

         The guard: each pending handoff is stored on the element so the opposite action can
         cancel it. */
      if (ans._accOpenEnd) {
        ans.removeEventListener('transitionend', ans._accOpenEnd);
        ans._accOpenEnd = null;
      }

      // If height is auto, lock to current height then collapse
      if (ans.style.height === 'auto') {
        ans.style.height = ans.scrollHeight + 'px';
        void ans.offsetHeight;
      }
      ans.style.height = '0px';
      ans.setAttribute('aria-hidden', 'true');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (icon) setIconState(icon, false);
      item.classList.remove('active');

      const onCloseEnd = function (e) {
        if (e.propertyName !== 'height') return;
        if (window.updateStickyButtonPosition) {
          window.updateStickyButtonPosition();
          if (window.updateStickyButtonVisibility) {
            window.updateStickyButtonVisibility();
          }
        }
        ans.removeEventListener('transitionend', onCloseEnd);
        ans._accCloseEnd = null;
      };
      ans._accCloseEnd = onCloseEnd;                       /* FD-22 guard */
      ans.addEventListener('transitionend', onCloseEnd);
    }

    function openItem(item) {
      const btn = item.querySelector('.accordion-question');
      const ans = item.querySelector('.accordion-answer');
      const icon = item.querySelector('.accordion-icon');
      if (!ans) return;

      /* FD-22 guard, mirror of the one in closeItem(): cancel a pending CLOSE handoff so a
         rapid close→open cannot leave a stale listener behind either. */
      if (ans._accCloseEnd) {
        ans.removeEventListener('transitionend', ans._accCloseEnd);
        ans._accCloseEnd = null;
      }

      ans.style.height = ans.scrollHeight + 'px';
      const onEnd = function (e) {
        if (e.propertyName !== 'height') return;
        ans.style.height = 'auto';
        if (window.updateStickyButtonPosition) {
          window.updateStickyButtonPosition();
          if (window.updateStickyButtonVisibility) {
            window.updateStickyButtonVisibility();
          }
        }
        ans.removeEventListener('transitionend', onEnd);
        ans._accOpenEnd = null;
      };
      ans._accOpenEnd = onEnd;                             /* FD-22 guard */
      ans.addEventListener('transitionend', onEnd);
      ans.setAttribute('aria-hidden', 'false');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      if (icon) setIconState(icon, true);
      item.classList.add('active');

      if (window.updateStickyButtonPosition) {
        requestAnimationFrame(() => {
          window.updateStickyButtonPosition();
          if (window.updateStickyButtonVisibility) {
            window.updateStickyButtonVisibility();
          }
        });
      }
    }

    items.forEach(item => {
      const btn = item.querySelector('.accordion-question');
      const ans = item.querySelector('.accordion-answer');
      if (!btn || !ans) return;
      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('active');
        // Close all others in the same accordion container
        items.forEach(other => {
          if (other !== item) closeItem(other);
        });
        // Toggle current
        if (isOpen) {
          closeItem(item);
        } else {
          openItem(item);
        }

        if (window.updateStickyButtonPosition) {
          requestAnimationFrame(() => {
            window.updateStickyButtonPosition();
            if (window.updateStickyButtonVisibility) {
              window.updateStickyButtonVisibility();
            }
          });
        }
      });
    });

    // Keep open panels responsive if window resizes
    const handleResize = () => {
      items.forEach(item => {
        const ans = item.querySelector('.accordion-answer');
        if (item.classList.contains('active') && ans) {
          ans.style.height = 'auto';
        }
      });
    };

    // Store resize handler on container for potential cleanup
    container._faqResizeHandler = handleResize;
    window.addEventListener('resize', handleResize);
  }

  // Find all accordion containers (supports multiple selectors)
  const containers = document.querySelectorAll('[data-accordion], .accordion-container');

  // Initialize each accordion independently
  containers.forEach(container => {
    initAccordion(container);
  });
});
